/**
 * Email / phone quick-actions, shared by the Clients list and the client-detail
 * page so both look and behave the same.
 *
 * Each control is a <button> (not an <a>) so it can live inside a card that is
 * itself a link: the click is stopped from bubbling to the card's navigation,
 * then the URL is handed to the OS — `mailto:` opens the mail composer, `tel:`
 * opens the call dialog.
 *
 * The `.rp-contact` label collapses to an icon-only button when its container
 * is too narrow to show the full address/number (see the container query in
 * index.css) — full text when there's room, icon alone when there isn't.
 */

import type { ReactNode } from "react";

import { formatPhoneUS } from "@re-platform/ui";

const MailIcon = (): ReactNode => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ flexShrink: 0 }}
  >
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <polyline points="3 7 12 13 21 7" />
  </svg>
);

const PhoneIcon = (): ReactNode => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ flexShrink: 0 }}
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/** "+15125550142" → "(512) 555-0142"; anything unexpected is shown verbatim.
 *
 * Re-exported from the shared implementation rather than kept as a second copy:
 * this one was display-only and admin-only, so the web app formatted the same
 * stored number differently, and neither constrained what could be TYPED in.
 * Imported as well as re-exported — ContactButtons below calls it. */
export const formatPhone = formatPhoneUS;

function go(e: React.MouseEvent, href: string) {
  e.preventDefault();
  e.stopPropagation();
  window.location.href = href;
}

/**
 * @param compact drop the border/background so the buttons read as inline links
 *                (used on the detail page where they sit under a field label).
 */
export function ContactButtons({
  email,
  phone,
  compact,
}: {
  email?: string | null;
  phone?: string | null;
  compact?: boolean;
}): ReactNode {
  if (!email && !phone) return null;
  const cls = compact ? "rp-contact rp-contact--plain" : "rp-contact";
  return (
    <span style={{ display: "inline-flex", gap: 6, minWidth: 0 }}>
      {email && (
        <button
          type="button"
          className={cls}
          title={`Email ${email}`}
          aria-label={`Email ${email}`}
          onClick={(e) => go(e, `mailto:${email}`)}
        >
          <MailIcon />
          <span className="rp-contact__label">{email}</span>
        </button>
      )}
      {phone && (
        <button
          type="button"
          className={cls}
          title={`Call ${phone}`}
          aria-label={`Call ${phone}`}
          onClick={(e) => go(e, `tel:${phone}`)}
        >
          <PhoneIcon />
          <span className="rp-contact__label">{formatPhone(phone)}</span>
        </button>
      )}
    </span>
  );
}
